// Pure, sequence-gated chat lifecycle reducer — the single owner of the chat
// `chatStatus` decision.
//
// The recurring stuck/phantom/cancel-never-settles bugs came from `chatStatus`
// being written by ~38 unsynchronized sites (live SSE, lagging REST snapshots,
// optimistic local edits) arbitrated by wall-clock timers. This reducer enforces
// one invariant instead: `chatStatus` is a pure function of the latest applied
// backend sequence and live SSE authority; it advances monotonically within a
// turn; any input older than what is applied, or from a superseded turn, is
// rejected (not applied). This is the direct port of LangGraph's scheduler gate
// (channel_versions[chan] > versions_seen[node][chan]).
//
// This module is PURE: no store, no timers, no imports of useAgentStore /
// useChatStreamStore. It only reuses the side-effect-free status predicates from
// agentStoreUtils. The store holds the returned state object and mirrors its
// derived `status` into the top-level `chatStatus` field.

import {
  _isActivelyRunningSessionStatus,
  _isKnownStreamableSessionStatus,
  _isTerminalForStreaming,
  _BUSY_CHAT_STATUSES,
} from "../../agentStoreUtils";

/**
 * @typedef {Object} ChatLifecycleState
 * @property {string|null} sessionId
 * @property {number} turnEpoch
 * @property {number} appliedSseSeq
 * @property {'ready'|'submitted'|'streaming'|'cancelling'|'error'} status
 * @property {boolean} sseAuthority
 * @property {boolean} terminalApplied
 */

/**
 * @typedef {
 *   | { kind: 'sse', sessionId: string, eventType: string, seq: number|null, backendStatus: string|null }
 *   | { kind: 'rest', sessionId: string, fetchEpoch: number, backendStatus: string|null }
 *   | { kind: 'optimistic', sessionId: string|null, action: 'startChat'|'send'|'resume'|'cancel'|'clearError'|'error' }
 *   | { kind: 'reset', sessionId: string|null }
 *   | { kind: 'connState', sessionId: string, state: 'connecting'|'reconnecting'|'connected'|'idle'|'disconnected' }
 * } ChatLifecycleInput
 */

/** @type {ChatLifecycleState} */
export const INITIAL_LIFECYCLE = {
  sessionId: null,
  turnEpoch: 0,
  appliedSseSeq: 0,
  status: "ready",
  sseAuthority: false,
  terminalApplied: false,
};

function accept(next, reason) {
  return { next, accepted: true, reason };
}

function reject(prev, reason) {
  return { next: prev, accepted: false, reason };
}

// §4.1 session gate: an input carrying a sessionId that differs from the one the
// lifecycle currently tracks is for a session the user navigated away from.
function isSessionMismatch(prev, input) {
  return Boolean(input.sessionId && prev.sessionId && input.sessionId !== prev.sessionId);
}

// §4.4 status derivation table — the single source of precedence, centralizing
// what the snapshot / status_changed / fetchSessionDetail branches each used to
// re-derive. Returns the target status string given a backend session status and
// the previous lifecycle status; returns prevStatus for "no change".
export function lifecycleStatusFromBackendStatus(backendStatus, prevStatus) {
  if (!backendStatus) return prevStatus;
  if (_isActivelyRunningSessionStatus(backendStatus)) {
    return "streaming";
  }
  if (_isTerminalForStreaming(backendStatus)) {
    return backendStatus === "ERROR" ? "error" : "ready";
  }
  // Known-streamable but waiting-on-external-input (waiting-for-*): unlock the
  // composer if we were busy, otherwise leave status untouched.
  if (_isKnownStreamableSessionStatus(backendStatus)) {
    return _BUSY_CHAT_STATUSES.has(prevStatus) ? "ready" : prevStatus;
  }
  // Unknown / non-streamable-non-terminal (e.g. CANCELLING): no change here;
  // cancelling is handled by the clamp in deriveStatus.
  return prevStatus;
}

// Computes the next status + terminalApplied for a backend-status-bearing input
// (SSE snapshot/status_changed/session_complete, or REST), applying the §4.4
// clamps: cancelling only clears on a terminal; never retreat streaming→submitted.
function deriveStatus(prev, backendStatus) {
  if (!backendStatus) {
    // Activity / content event without a status (tool.*, message_added, ui_chunk):
    // first sign of life advances submitted→streaming (replaces _markSubmittedStreamStarted)
    // and clears terminalApplied — observing live activity means a new turn is
    // running, so the REST gate (§4.3) must protect it again.
    if (prev.status === "submitted") {
      return { status: "streaming", terminalApplied: false };
    }
    return { status: prev.status, terminalApplied: prev.terminalApplied };
  }
  const isTerminal = _isTerminalForStreaming(backendStatus);
  // cancelling is only cleared by an authoritative terminal.
  if (prev.status === "cancelling" && !isTerminal) {
    return { status: "cancelling", terminalApplied: prev.terminalApplied };
  }
  let next = lifecycleStatusFromBackendStatus(backendStatus, prev.status);
  // Monotonic clamp: a stale/duplicate "submitted" intent cannot pull a live
  // stream backwards.
  if (prev.status === "streaming" && next === "submitted") {
    next = "streaming";
  }
  // terminalApplied tracks "an authoritative terminal was seen and no newer live
  // activity has happened since". A terminal sets it; observing an actively-running
  // status clears it (a session can reactivate out-of-band, e.g. artifact submitted
  // elsewhere flips COMPLETED → IN_PROGRESS). This keeps the REST gate accurate
  // across reactivation without a wall-clock guard.
  let terminalApplied = prev.terminalApplied;
  if (isTerminal) {
    terminalApplied = true;
  } else if (next === "streaming") {
    terminalApplied = false;
  }
  return { status: next, terminalApplied };
}

// §4.6a reset — session switch / new chat / clear / agent change / invalidation.
function applyReset(prev, input) {
  return accept(
    {
      sessionId: input.sessionId ?? null,
      turnEpoch: prev.turnEpoch + 1,
      appliedSseSeq: 0,
      status: "ready",
      sseAuthority: false,
      // A fresh epoch has observed no terminal yet, so terminalApplied is false:
      // a cold-load REST of a still-running session (e.g. SSE unavailable) must be
      // accepted to render the transcript. Live-turn protection comes from
      // sseAuthority (the restSupersededByLiveSse branch) once SSE connects, and
      // the terminal flag is set only when an actual terminal status is observed.
      terminalApplied: false,
    },
    "reset"
  );
}

// §4.6 optimistic turn openers + cancel/clearError/error.
function applyOptimistic(prev, input) {
  const action = input.action;
  const isOpener = action === "startChat" || action === "send" || action === "resume";
  if (!isOpener && isSessionMismatch(prev, input)) {
    return reject(prev, "sessionMismatch");
  }
  if (isOpener) {
    // send/resume always carry the existing session id; startChat has none yet
    // (it is learned from the create response), so fall back to null — NOT the
    // previous session id, which would make the new turn's SSE events fail the
    // session gate. The id is adopted from the first SSE/connState event (§4.1).
    return accept(
      {
        ...prev,
        sessionId: input.sessionId ?? null,
        turnEpoch: prev.turnEpoch + 1,
        appliedSseSeq: 0,
        terminalApplied: false,
        status: "submitted",
      },
      `optimistic:${action}`
    );
  }
  if (action === "cancel") {
    return accept({ ...prev, status: "cancelling" }, "optimistic:cancel");
  }
  if (action === "clearError") {
    return accept({ ...prev, status: "ready", terminalApplied: true }, "optimistic:clearError");
  }
  if (action === "error") {
    return accept({ ...prev, status: "error" }, "optimistic:error");
  }
  return reject(prev, `unknownOptimistic:${action}`);
}

// §4.8 connState — drives the SSE-authority flag used by the REST gate (§4.3).
function applyConnState(prev, input) {
  if (isSessionMismatch(prev, input)) {
    return reject(prev, "sessionMismatch");
  }
  const authoritative =
    input.state === "connected" || input.state === "connecting" || input.state === "reconnecting";
  // Adopt the session id if the lifecycle does not have one yet (e.g. after a
  // startChat opener cleared it). connState is the earliest event of a fresh
  // connection.
  const sessionId = prev.sessionId ?? input.sessionId ?? null;
  if (prev.sseAuthority === authoritative && prev.sessionId === sessionId) {
    return reject(prev, "connStateNoChange");
  }
  return accept({ ...prev, sessionId, sseAuthority: authoritative }, `connState:${input.state}`);
}

// §4.2 SSE staleness gate + status derivation. The literal port of LangGraph's
// version > seen gate.
function applySse(prev, input) {
  if (isSessionMismatch(prev, input)) {
    return reject(prev, "sessionMismatch");
  }
  // Connect-time snapshots often carry no SSE seq. After a follow-up send on a
  // COMPLETED session, the backend can still deliver that stale COMPLETED snapshot
  // before the new message is processed — clearing submitted→ready and flickering
  // the thinking indicator. Reject until at least one sequenced live event lands.
  if (
    input.eventType === "snapshot" &&
    prev.status === "submitted" &&
    prev.appliedSseSeq === 0 &&
    !prev.terminalApplied &&
    _isTerminalForStreaming(input.backendStatus ?? null)
  ) {
    return reject(prev, "staleConnectSnapshotDuringSubmittedTurn");
  }
  let appliedSseSeq = prev.appliedSseSeq;
  if (typeof input.seq === "number" && Number.isFinite(input.seq)) {
    if (input.seq <= prev.appliedSseSeq) {
      return reject(prev, `staleSse:${input.seq}<=${prev.appliedSseSeq}`);
    }
    appliedSseSeq = input.seq;
  }
  const { status, terminalApplied } = deriveStatus(prev, input.backendStatus ?? null);
  // Adopt the session id if the lifecycle does not have one yet (the startChat
  // opener clears it; the first event of the new turn establishes identity so
  // the session gate engages for the rest of the turn).
  const sessionId = prev.sessionId ?? input.sessionId ?? null;
  return accept(
    { ...prev, sessionId, appliedSseSeq, status, terminalApplied },
    `sse:${input.eventType}:backend=${input.backendStatus ?? "none"}:seq=${input.seq ?? "none"}`
  );
}

// §4.3 REST authority gate — REST is gated by authority + epoch, never by numeric
// comparison to appliedSseSeq (the two backend sequences are different number spaces).
function applyRest(prev, input) {
  if (isSessionMismatch(prev, input)) {
    return reject(prev, "sessionMismatch");
  }
  if (input.fetchEpoch !== prev.turnEpoch) {
    return reject(prev, "staleRestEpoch");
  }
  const backendStatus = input.backendStatus ?? null;
  const isTerminal = _isTerminalForStreaming(backendStatus);
  // The §4.4 derivation applied to the REST status — shared by every accept path.
  const applied = () => {
    const { status, terminalApplied } = deriveStatus(prev, backendStatus);
    return { ...prev, status, terminalApplied };
  };

  // An authoritative terminal always reconciles (idempotent) regardless of who is
  // the live authority — it is the backstop that settles a turn even when the
  // session_complete SSE never arrived. The reason records which gate it passed.
  if (isTerminal) {
    const reason = prev.terminalApplied
      ? `rest:terminalReconcile:${backendStatus}`
      : prev.sseAuthority && prev.appliedSseSeq > 0
        ? `rest:terminalBackstop:${backendStatus}`
        : `rest:backend=${backendStatus}`;
    return accept(applied(), reason);
  }

  // Non-terminal REST must not resurrect a settled or live turn.
  if (prev.terminalApplied) {
    // A terminal was already applied this epoch (and no newer live activity reset
    // it — see deriveStatus). A non-terminal REST is a lagging snapshot captured
    // before the session finished persisting; applying it would reactivate a
    // completed turn ("post-completion overwrite"). Replaces the 5s
    // POST_COMPLETE_REST_GUARD_MS window with a deterministic flag.
    return reject(prev, "restAfterTerminal");
  }
  if (prev.sseAuthority && prev.appliedSseSeq > 0) {
    // SSE is the live authority for a still-running turn; a non-terminal REST is a
    // lagging snapshot and must not overwrite live state.
    return reject(prev, "restSupersededByLiveSse");
  }
  // SSE is not the live authority (cold load, poll fallback while disconnected)
  // and no terminal seen yet: REST legitimately drives status.
  return accept(applied(), `rest:backend=${backendStatus ?? "none"}`);
}

/**
 * Reduce one input against the current lifecycle state.
 * @param {ChatLifecycleState} prev
 * @param {ChatLifecycleInput} input
 * @returns {{ next: ChatLifecycleState, accepted: boolean, reason: string }}
 */
export function reduceChatLifecycle(prev, input) {
  if (!input || !input.kind) {
    return reject(prev, "noInput");
  }
  switch (input.kind) {
    case "reset":
      return applyReset(prev, input);
    case "optimistic":
      return applyOptimistic(prev, input);
    case "connState":
      return applyConnState(prev, input);
    case "sse":
      return applySse(prev, input);
    case "rest":
      return applyRest(prev, input);
    default:
      return reject(prev, `unknownKind:${input.kind}`);
  }
}
